import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import useSolutionDetails from "../hooks/useSolutionDetails";
import ErrorFallback from "../../common/ErrorFallback";
import SuccessRibbon from "../../common/SuccessRibbon";
import { ReadOnlyCountries } from "./ReadOnlyCountry";
import { ReadOnlyCoordinates } from "./ReadOnlyCoordinates";
import { ReadOnlyDates } from "./ReadOnlyDates";
import { ReadOnlySDG } from "./ReadOnlySDG";
import { ReadOnlyReadyForSaleRadio } from "./ReadOnlyReadyForSaleRadio";
import ReadOnlyInterventionsContainer from "./ReadOnlyInterventionsContainer";
import ReadOnlyOutcomesContainer from "./ReadOnlyOutcomesContainer";
import ReadOnlyCarbonOffsetting from "./ReadOnlyCarbonOffsetting";
import ReadOnlyProgrammeInfoContainer from "./ReadOnlyProgrammeInfoContainer";
import ReadOnlyInternalDetailsContainer from "./ReadOnlyInternalDetailsContainer";
import { UpdateSolutionDetailsDrawer } from "./update-solution";
import ErrorRibbon from "../../common/ErrorRibbon";
import { useNavigate, useParams } from "react-router";
import ReadOnlyRiskIncidentSection from "./incident-section/ReadOnlyRiskIncidentSection";
import { SyntheticEvent, useState } from "react";
import TabSection from "../../common/TabSection";

function tabInfo(index: number) {
  return {
    id: `solution-details-tab-${index}`,
    "aria-controls": `solution-details-tabpanel-${index}`,
  };
}

function SolutionDetails() {
  const { id: solutionId } = useParams<{ id: string }>();
  if (!solutionId) {
    throw new Error("Solution ID is required");
  }
  const navigate = useNavigate();
  const { details, error, loading, updateSolutionDetails, linkToStripe } =
    useSolutionDetails(solutionId);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (error) {
    return (
      <>
        <ErrorFallback error={error} />
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </>
    );
  }

  if (loading) {
    return (
      <Box
        data-testid="solution-details-loading-indicator"
        sx={{
          minHeight: "80vh",
          display: "flex",
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (details) {
    const {
      id,
      name,
      vbcWebsiteDisplayName,
      developer,
      shortDescription,
      assessmentMethodology,
      interventionType,
      country,
      isReadyForSale,
      isUsedForCarbonOffsetting,
      SDG,
      developmentStage,
      date,
      coordinates,
      interventions,
      outcomes,
      programmeInfo,
      internalUseOnly,
      riskMonitoring,
    } = details;
    const formattedDevelopmentStage = Array.isArray(developmentStage)
      ? developmentStage.join(", ")
      : developmentStage || "-";

    return (
      <>
        <SuccessRibbon
          data={{
            details: updateSolutionDetails.alert,
            setAlert: updateSolutionDetails.setAlert,
          }}
        />
        <SuccessRibbon
          data={{
            details: linkToStripe.alert,
            setAlert: linkToStripe.setAlert,
          }}
        />
        <ErrorRibbon
          data={{
            details: linkToStripe.alert,
            setAlert: linkToStripe.setAlert,
          }}
        />
        <UpdateSolutionDetailsDrawer
          loading={updateSolutionDetails.loading}
          isEditing={updateSolutionDetails.isEditing}
          handleEdit={(value) => updateSolutionDetails.setEditing(value)}
          updateSolution={updateSolutionDetails.update}
          errorAlert={{
            details: updateSolutionDetails.alert,
            setAlert: updateSolutionDetails.setAlert,
          }}
          solutionDetails={details}
        />
        <Box
          data-testid="solution-details-page-container-outer"
          sx={{
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            data-testid="solution-details-page-container-inner"
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: 1200,
              px: 2,
            }}
          >
            <Button
              data-testid="edit-solution-button"
              variant="contained"
              size="medium"
              onClick={() => {
                updateSolutionDetails.setAlert(null);
                updateSolutionDetails.setEditing(true);
              }}
              sx={{
                alignSelf: "flex-end",
                mb: 2,
              }}
            >
              Edit Project
            </Button>
            <Box data-testid="fields-container" sx={{ width: "100%" }}>
              <Typography
                id="solution-name-header"
                sx={{ paddingBottom: 1 }}
                align="left"
                variant="h4"
              >
                {name}
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="solution details tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Overview" {...tabInfo(0)} />
                  <Tab label="Incident Monitoring" {...tabInfo(1)} />
                </Tabs>
              </Box>

              <TabSection value={tabValue} index={0}>
                <Box sx={{ paddingTop: 1, paddingRight: 2 }}>
                  <ReadOnlyCountries code={country} />
                </Box>
                <Box
                  sx={{ display: "flex", direction: "row", paddingBottom: 2 }}
                >
                  <Box sx={{ paddingRight: 2 }}>
                    <ReadOnlyReadyForSaleRadio isReady={isReadyForSale} />
                  </Box>
                  <Box sx={{ paddingRight: 2 }}>
                    <ReadOnlyCarbonOffsetting
                      usedToOffset={isUsedForCarbonOffsetting}
                    />
                  </Box>
                </Box>
                <Box sx={{ paddingBottom: 1 }} maxWidth="fit-content">
                  <ReadOnlySDG sdg={SDG} />
                </Box>
                <TextField
                  value={id}
                  variant="standard"
                  id="solution-id"
                  label="ID"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <TextField
                  value={vbcWebsiteDisplayName || "-"}
                  variant="standard"
                  id="solution-vbc-website-display-name"
                  label="VBC Website Display Name"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <TextField
                  value={shortDescription || "-"}
                  variant="standard"
                  multiline
                  rows={shortDescription === "-" ? 1 : 4}
                  id="solution-short-description"
                  label="Short Description"
                  size="small"
                  sx={{
                    marginBottom: 2,
                    minWidth: "100%",
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <TextField
                  value={developer || "-"}
                  variant="standard"
                  id="solution-developer"
                  label="Developer"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <TextField
                  value={formattedDevelopmentStage || []}
                  variant="standard"
                  id="solution-development-stage"
                  label="Development Stage"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />

                <TextField
                  value={assessmentMethodology || "-"}
                  variant="standard"
                  id="solution-assessment-methodology"
                  label="Assessment Methodology"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <TextField
                  value={interventionType || "-"}
                  variant="standard"
                  id="solution-intervention-type"
                  label="Intervention Type"
                  size="small"
                  fullWidth
                  sx={{
                    marginBottom: 2,
                  }}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                />
                <Box sx={{ paddingBottom: 2 }}>
                  <ReadOnlyDates start={date?.start} end={date?.end} />
                </Box>
                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyCoordinates
                    latitude={coordinates?.latitude}
                    longitude={coordinates?.longitude}
                  />
                </Box>

                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyInterventionsContainer
                    interventions={interventions}
                  />
                </Box>
                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyOutcomesContainer outcomes={outcomes} />
                </Box>
                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyProgrammeInfoContainer
                    programmeInfo={programmeInfo}
                  />
                </Box>

                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyInternalDetailsContainer
                    internalDetails={internalUseOnly}
                    linkToStripe={linkToStripe}
                  />
                </Box>
              </TabSection>

              <TabSection value={tabValue} index={1}>
                <Box sx={{ paddingBottom: 4 }}>
                  <ReadOnlyRiskIncidentSection
                    solutionId={solutionId}
                    riskMonitoringInfo={riskMonitoring}
                  />
                </Box>
              </TabSection>
            </Box>
          </Box>
        </Box>
      </>
    );
  }
  return null;
}

export default SolutionDetails;
